import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";

export interface SavedSectionBlock {
  type: string;
  props: Record<string, unknown>;
}

export interface SavedSectionPuckData {
  content?: SavedSectionBlock[];
  root?: Record<string, unknown>;
  zones?: Record<string, SavedSectionBlock[]>;
  [key: string]: unknown;
}

export interface SavedSectionRecord {
  id: string;
  name: string;
  category: string;
  puckData: SavedSectionPuckData;
  thumbnail: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function fetchSavedSections(): Promise<SavedSectionRecord[]> {
  const response = await apiFetch<{ data: { items: SavedSectionRecord[] } }>("/crm/pages/sections?limit=100");
  return response.data.items ?? [];
}

export async function createSavedSection(input: {
  name: string;
  category?: string;
  puckData: SavedSectionPuckData;
  thumbnail?: string | null;
}): Promise<SavedSectionRecord> {
  const response = await apiFetch<{ data: SavedSectionRecord }>("/crm/pages/sections", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function deleteSavedSection(id: string): Promise<void> {
  await apiFetch(`/crm/pages/sections/${id}`, { method: "DELETE" });
}

export async function recordSavedSectionUse(id: string): Promise<void> {
  await apiFetch(`/crm/pages/sections/${id}/usage`, { method: "POST" });
}

export function useSavedSections() {
  const [sections, setSections] = useState<SavedSectionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      setSections(await fetchSavedSections());
    } catch (error) {
      setSections([]);
      setError(error instanceof ApiError ? error.message : "Failed to load saved sections");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { sections, isLoading, error, refresh };
}
